// Considered: AddSphere molecule — rejected because it renders a full dialog form,
// not a lightweight inline row for quick name-capture of a draft sphere.
import React, { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAnalytics } from '@center-technologies/analytics';
import { Sphere } from 'types/global';
import type { RootState } from '@center-pwa/store/store';
import './InlineAddSphereRowMolecule.scss';

const GENERIC_SPHERE_SCHEMA = 'center_sphere';

export interface InlineAddSphereRowMoleculeProps {
  parentSphere: Sphere;
  /** Called after a draft sphere is successfully captured. Receives the new sphere. */
  onCapture: (sphere: Sphere) => void;
  /** Called when type-selector or "Use a template…" is clicked. */
  onOpenDialog: (tab: 'blank' | 'template') => void;
  onCancel: () => void;
}

const InlineAddSphereRowMolecule: React.FC<InlineAddSphereRowMoleculeProps> = ({
  parentSphere,
  onCapture,
  onOpenDialog,
  onCancel,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const logAnalyticsEvent = useAnalytics();
  const userSphere = useSelector((state: RootState) => state.userProfile.userSphere);
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCapture = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || !apiClients?.graphClient || isLoading) return;

    setIsLoading(true);
    setError(null);
    try {
      // Canonical create path — same as every other sphere we create (and the e2e
      // suites): POST /spheres/init runs initializeSphere SERVER-SIDE, where the graph
      // can resolve center_center/center_parent from the parent (the Atlas tree node
      // the client holds has no assignedPerspectives). Then POST /spheres persists it.
      // Draft mode skips required-field validation + arc deploy.
      const initResp = await apiClients.graphClient.post<{ sphere?: Sphere }>('/spheres/init', {
        schemaSystemName: GENERIC_SPHERE_SCHEMA,
        centerSphereId: parentSphere.id,
        userSphereId: userSphere?.id,
        overrides: { properties: { name: trimmedName } },
      });

      const initializedSphere = initResp.sphere;
      if (!initializedSphere) {
        setError(t('addSphere.inlineAdd.captureError'));
        return;
      }

      const resp = await apiClients.graphClient.post<{
        status: string;
        sphere?: Sphere;
      }>('/spheres', {
        draft: true,
        schemaSystemName: GENERIC_SPHERE_SCHEMA,
        sphere: initializedSphere,
      });

      if (resp.sphere) {
        logAnalyticsEvent('draft_sphere_captured', undefined, {
          sphereId: resp.sphere.id,
          parentId: parentSphere.id,
          source: 'inline',
        });
        onCapture(resp.sphere);
        setName('');
      } else {
        setError(t('addSphere.inlineAdd.captureError'));
      }
    } catch {
      setError(t('addSphere.inlineAdd.captureError'));
    } finally {
      setIsLoading(false);
    }
  }, [name, apiClients, isLoading, userSphere, parentSphere, onCapture, logAnalyticsEvent, t]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleCapture();
      if (e.key === 'Escape') onCancel();
    },
    [handleCapture, onCancel],
  );

  return (
    <div className="inline-add-sphere-row" data-testid="inline-add-sphere-row">
      <div className="inline-add-sphere-row__indent" aria-hidden="true">
        <i className="pi pi-arrow-down-right" />
      </div>

      <button
        type="button"
        className="inline-add-sphere-row__type-btn"
        onClick={() => onOpenDialog('blank')}
        title={t('addSphere.inlineAdd.chooseSphereType')}
      >
        <i className="pi pi-circle" />
        <span>{t('addSphere.inlineAdd.typeSphere')}</span>
        <i className="pi pi-chevron-down" />
      </button>

      <input
        ref={inputRef}
        autoFocus
        className="inline-add-sphere-row__name-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('addSphere.inlineAdd.namePlaceholder')}
        disabled={isLoading}
        aria-label={t('addSphere.inlineAdd.namePlaceholder')}
      />

      {error && (
        <span className="inline-add-sphere-row__error" role="alert">
          {error}
        </span>
      )}

      <div className="inline-add-sphere-row__actions">
        <button
          type="button"
          className="inline-add-sphere-row__template-btn"
          onClick={() => onOpenDialog('template')}
        >
          <i className="pi pi-th-large" />
          {t('addSphere.inlineAdd.useTemplate')}
        </button>

        <button
          type="button"
          className="inline-add-sphere-row__capture-btn"
          onClick={handleCapture}
          disabled={!name.trim() || isLoading}
        >
          <i className="pi pi-arrow-down-left" />
          {t('addSphere.inlineAdd.capture')}
        </button>

        <button
          type="button"
          className="inline-add-sphere-row__cancel-btn"
          onClick={onCancel}
          aria-label={t('common.actions.cancel')}
        >
          <i className="pi pi-times" />
        </button>
      </div>
    </div>
  );
};

export default InlineAddSphereRowMolecule;
